import React from 'react';

var FormContext = React.createContext({
    fieldErrors: {},
    formErrors: {},
    isDirty: false,
    isSubmitting: false,
    values: {},
    clearForm: function () { },
    dispatchFieldError: function () { },
    dispatchFormError: function () { },
    dispatchValue: function () { },
});

var useErrors = function (fieldId) {
    var _a = React.useContext(FormContext), fieldErrors = _a.fieldErrors, formErrors = _a.formErrors;
    return fieldId
        ? fieldErrors[fieldId]
        : formErrors;
};

var useField = function (id, callback) {
    var isMounted = React.useRef(false);
    var context = React.useContext(FormContext);
    var addError = React.useCallback(function (key, error) {
        context.dispatchFieldError({
            type: "form.action.add" /* ADD */, id: id, key: key, value: error
        });
    }, []);
    var removeError = React.useCallback(function (key) {
        context.dispatchFieldError({
            type: "form.action.remove" /* REMOVE */, id: id, key: key
        });
    }, []);
    var setValue = React.useCallback(function (value) {
        context.dispatchValue({ type: "form.action.add" /* ADD */, id: id, value: value });
    }, []);
    var fieldCtx = {
        errors: context.fieldErrors[id] || {},
        value: context.values[id],
        addError: addError,
        removeError: removeError,
        setValue: setValue
    };
    React.useEffect(function () {
        if (isMounted.current && callback)
            callback(fieldCtx);
        isMounted.current = true;
    }, [fieldCtx.value]);
    return fieldCtx;
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var useFormContext = function () {
    var _a = React.useContext(FormContext), dispatchFieldError = _a.dispatchFieldError, dispatchFormError = _a.dispatchFormError, dispatchValue = _a.dispatchValue, values = _a.values, ctx = __rest(_a, ["dispatchFieldError", "dispatchFormError", "dispatchValue", "values"]);
    var addFieldError = function (id, key, value) { return dispatchFieldError({
        type: "form.action.add" /* ADD */, id: id, key: key, value: value
    }); };
    var addFormError = function (key, value) { return dispatchFormError({
        type: "form.action.add" /* ADD */, key: key, value: value
    }); };
    var clearFieldErrors = function (id) { return dispatchFieldError({
        type: "form.action.clear" /* CLEAR */, id: id
    }); };
    var clearFormErrors = function () { return dispatchFormError({ type: "form.action.clear" /* CLEAR */ }); };
    var removeFieldError = function (id, key) { return dispatchFieldError({
        type: "form.action.remove" /* REMOVE */, id: id, key: key
    }); };
    var removeFormError = function (key) { return dispatchFormError({
        type: "form.action.remove" /* REMOVE */, key: key
    }); };
    var setValue = function (id, value) { return dispatchValue({
        type: "form.action.add" /* ADD */, id: id, value: value
    }); };
    return __assign(__assign({}, ctx), { values: values,
        addFieldError: addFieldError,
        addFormError: addFormError,
        clearFieldErrors: clearFieldErrors,
        clearFormErrors: clearFormErrors,
        removeFieldError: removeFieldError,
        removeFormError: removeFormError,
        setValue: setValue });
};

var fieldErrorsReducer = function (state, action) {
    var _a, _b, _c, _d;
    switch (action.type) {
        case "form.action.add" /* ADD */:
            var otherErrors = state[action.id] || {};
            return __assign(__assign({}, state), (_a = {}, _a[action.id] = __assign(__assign({}, otherErrors), (_b = {}, _b[action.key] = action.value, _b)), _a));
        case "form.action.clear" /* CLEAR */:
            return __assign(__assign({}, state), (_c = {}, _c[action.id] = {}, _c));
        case "form.action.remove" /* REMOVE */:
            var _e = state[action.id] || {}, _f = action.key, removed = _e[_f], remainingErrors = __rest(_e, [typeof _f === "symbol" ? _f : _f + ""]);
            return __assign(__assign({}, state), (_d = {}, _d[action.id] = remainingErrors, _d));
        case "form.action.reset" /* RESET */:
            return {};
    }
};

var formErrorsReducer = function (state, action) {
    var _a;
    switch (action.type) {
        case "form.action.add" /* ADD */:
            return __assign(__assign({}, state), (_a = {}, _a[action.key] = action.value, _a));
        case "form.action.remove" /* REMOVE */:
            var _b = state, _c = action.key, removed = _b[_c], remainingErrors = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
            return remainingErrors;
        case "form.action.reset" /* RESET */:
        case "form.action.clear" /* CLEAR */:
            return {};
    }
};

var valuesReducer = function (state, action) {
    var _a;
    switch (action.type) {
        case "form.action.add" /* ADD */:
            return __assign(__assign({}, state), (_a = {}, _a[action.id] = action.value, _a));
        case "form.action.reset" /* RESET */:
            return action.initialValues;
    }
};

var hasFieldErrors = function (fieldErrors) { return (Object.keys(fieldErrors).reduce(function (acc, elem) { return (acc || Object.keys(fieldErrors[elem]).length > 0); }, false)); };
var Form = function (_a) {
    var children = _a.children, _b = _a.disabled, disabled = _b === void 0 ? false : _b, _c = _a.initialValues, initialValues = _c === void 0 ? {} : _c, _d = _a.keepFormErrorsOnSubmit, keepFormErrorsOnSubmit = _d === void 0 ? false : _d, render = _a.render, onSubmit = _a.onSubmit, onSubmitWithErrors = _a.onSubmitWithErrors, rest = __rest(_a, ["children", "disabled", "initialValues", "keepFormErrorsOnSubmit", "render", "onSubmit", "onSubmitWithErrors"]);
    var isDirty = React.useRef(false);
    var _e = React.useState(false), isSubmitting = _e[0], setIsSubmitting = _e[1];
    var _f = React.useReducer(fieldErrorsReducer, {}), fieldErrors = _f[0], dispatchFieldError = _f[1];
    var _g = React.useReducer(formErrorsReducer, {}), formErrors = _g[0], dispatchFormError = _g[1];
    var _h = React.useReducer(valuesReducer, initialValues), values = _h[0], dispatchValue = _h[1];
    var clearForm = React.useCallback(function () {
        isDirty.current = false;
        dispatchFieldError({ type: "form.action.reset" /* RESET */ });
        dispatchFormError({ type: "form.action.reset" /* RESET */ });
        dispatchValue({ type: "form.action.reset" /* RESET */, initialValues: initialValues });
    }, []);
    var triggerSubmit = React.useCallback(function (ev) {
        if (disabled)
            return;
        if (ev) {
            ev.preventDefault();
            ev.stopPropagation();
        }
        setIsSubmitting(true);
    }, [disabled]);
    React.useEffect(function () {
        if (!isSubmitting)
            return;
        if (!keepFormErrorsOnSubmit)
            dispatchFormError({ type: "form.action.clear" /* CLEAR */ });
        var setFormError = function (key, value) {
            if (value) {
                dispatchFormError({ type: "form.action.add" /* ADD */, key: key, value: value });
            }
            else {
                dispatchFormError({ type: "form.action.remove" /* REMOVE */, key: key });
            }
        };
        var submitResult = hasFieldErrors(fieldErrors)
            ? onSubmitWithErrors(values, fieldErrors, { clearForm: clearForm, setFormError: setFormError })
            : onSubmit(values, { clearForm: clearForm, setFormError: setFormError });
        submitResult.finally(function () { return setIsSubmitting(false); });
    }, [isSubmitting]);
    var value = {
        formErrors: formErrors,
        fieldErrors: fieldErrors,
        isDirty: isDirty.current,
        isSubmitting: isSubmitting,
        values: values,
        clearForm: clearForm,
        dispatchFieldError: dispatchFieldError,
        dispatchFormError: dispatchFormError,
        dispatchValue: dispatchValue
    };
    return (React.createElement(FormContext.Provider, { value: value }, render
        ? render({ onSubmit: triggerSubmit })
        : React.createElement("form", __assign({}, rest, { onSubmit: triggerSubmit }), children)));
};

export default Form;
export { useErrors, useField, useFormContext };
//# sourceMappingURL=index.es.js.map
